// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AuditLoggers } from '../utils/auditLogger';
import { clearServerIdentity } from '../auth/serverIdentity';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  // if (!context) {
  //   throw new Error('useAuth must be used within an AuthProvider');
  // }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.log('Token is not JWT format, assuming valid');
        return false;
      }
      
      // Use a more reliable method to decode base64
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const payload = JSON.parse(jsonPayload);
      if (!payload.exp) {
        console.log('Token has no expiration, assuming valid');
        return false;
      }
      
      const currentTime = Date.now() / 1000;
      const isExpired = payload.exp < currentTime;
      console.log('Token expiration check:', { 
        tokenExp: payload.exp, 
        currentTime, 
        isExpired,
        timeLeft: payload.exp - currentTime 
      });
      return isExpired;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      // If we can't parse the token, assume it's valid to avoid false logouts
      return false;
    }
  };

  // Validate authentication - only when needed
  const validateAuth = useCallback(() => {
    try {
      const token = localStorage.getItem('token');
      const isLoggedIn = localStorage.getItem('isLoggedIn');
      const username = localStorage.getItem('username');

      console.log('Validating auth:', { 
        hasToken: !!token, 
        isLoggedIn, 
        username,
        tokenLength: token?.length 
      });

      // If no token, user is not authenticated
      if (!token) {
        console.log('No token found');
        return false;
      }

      // Check if user is marked as logged in
      if (isLoggedIn !== 'true') {
        console.log('User not marked as logged in');
        return false;
      }

      // Check token expiration
      if (isTokenExpired(token)) {
        console.log('Token is expired');
        return false;
      }

      // Check session duration (24 hours)
      const loginTime = localStorage.getItem('loginTime');
      if (loginTime) {
        const loginTimeStamp = parseInt(loginTime);
        const currentTime = new Date().getTime();
        const sessionDuration = 24 * 60 * 60 * 1000; // 24 hours
        
        if (currentTime - loginTimeStamp > sessionDuration) {
          console.log('Session expired (24 hours)');
          return false;
        }
      }

      console.log('Auth validation successful');
      return true;
    } catch (error) {
      console.error('Error during auth validation:', error);
      // If there's an error during validation, assume valid to avoid false logouts
      return true;
    }
  }, []);

  // Clear auth data
  const clearAuthData = useCallback(() => {
    console.log('Clearing invalid auth data');
    localStorage.removeItem('token');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('username');
    localStorage.removeItem('loginTime');
    localStorage.removeItem('application');
    localStorage.removeItem('user_id');
    clearServerIdentity();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Initialize authentication state - ONLY check if token exists
  useEffect(() => {
    const initAuth = () => {
      console.log('Initializing auth...');
      setIsLoading(true);
      
      try {
        const token = localStorage.getItem('token');
        const isLoggedIn = localStorage.getItem('isLoggedIn');
        const username = localStorage.getItem('username');
        
        // Only validate if there's actually token data
        if (token && isLoggedIn === 'true' && username) {
          console.log('Found existing auth data, validating...');
          const isValid = validateAuth();
          
          if (isValid) {
            console.log('Existing auth is valid');
            setUser({ username });
            setIsAuthenticated(true);
          } else {
            console.log('Existing auth is invalid, clearing...');
            clearAuthData();
          }
        } else {
          console.log('No existing auth data found');
          // Don't clear data here, just set as not authenticated
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
        setHasInitialized(true);
        console.log('Auth initialization complete');
      }
    };

    initAuth();
  }, []); // Only run once on mount

  // Login function
  const login = useCallback(async (token, userData) => {
    console.log('Login function called with:', { 
      hasToken: !!token, 
      userData,
      tokenLength: token?.length 
    });
    
    try {
      if (!token) {
        console.error('No token provided to login function');
        return false;
      }

      // Store auth data in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('username', userData?.username || 'User');
      localStorage.setItem('loginTime', new Date().getTime().toString());
      localStorage.setItem('application', 'Conductor');
      
      // Store user_id if available
      if (userData?.id) {
        localStorage.setItem('user_id', userData.id.toString());
      }
      
      console.log('Auth data stored in localStorage');
      
      // Update state
      const userInfo = {
        username: userData?.username || 'User',
        ...userData
      };
      
      setUser(userInfo);
      setIsAuthenticated(true);
      
      console.log('Login successful, auth state updated');
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    console.log('Logging out...');
    const token = localStorage.getItem('token');
    const username = localStorage.getItem('username');
    const tenantId = localStorage.getItem('tenant_id');
    
    try {
      // Log the logout action before making the API call
      if (username) {
        try {
          await AuditLoggers.userLogout(username, {
            userId: localStorage.getItem('user_id'),
            tenantId,
            sessionEnd: new Date().toISOString()
          });
        } catch (logError) {
          console.error('Failed to log logout event:', logError);
          // Don't block logout if logging fails
        }
      }
      
      if (token) {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('Logout API successful:', data);
        } else {
          console.warn('Logout API returned non-200 status:', response.status);
        }
      }
    } catch (err) {
      console.error('Logout API error:', err);
      // Continue with logout even if API call fails
    } finally {
      // Clear all auth-related data
      clearAuthData();
      
      // Clear additional data that might be stored
      localStorage.removeItem('tenant_id');
      localStorage.removeItem('tenant_integration');
      localStorage.removeItem('tenant_name');
      localStorage.removeItem('first_name');
      localStorage.removeItem('last_name');
      localStorage.removeItem('email');
      localStorage.removeItem('harmonate_user');
      
      // Clear console (this will help with debugging)
      console.clear();
      console.log('User logged out successfully');
      
      // Dispatch a custom event for logout success
      window.dispatchEvent(new CustomEvent('logout-success'));
      console.log('Logout complete');
    }
  }, [clearAuthData]);

  // Debug: Log state changes
  useEffect(() => {
    if (hasInitialized) {
      console.log('AuthContext state changed:', { 
        isAuthenticated, 
        isLoading, 
        user: !!user,
        username: user?.username
      });
    }
  }, [isAuthenticated, isLoading, user, hasInitialized]);

  // Periodic token validation - ONLY when authenticated and not loading
  useEffect(() => {
    if (isAuthenticated && !isLoading && hasInitialized) {
      console.log('Setting up periodic token validation...');
      
      const interval = setInterval(() => {
        console.log('Running periodic token validation...');
        
        // Only validate if we're still authenticated (double-check)
        if (!isAuthenticated) {
          console.log('User no longer authenticated, skipping validation');
          return;
        }
        
        const isValid = validateAuth();
        if (!isValid) {
          console.log('Periodic validation failed, logging out...');
          logout();
        } else {
          console.log('Periodic validation passed');
        }
      }, 10 * 60 * 1000); // Check every 10 minutes

      return () => {
        console.log('Clearing periodic validation interval');
        clearInterval(interval);
      };
    }
  }, [isAuthenticated, isLoading, hasInitialized, validateAuth, logout]);

  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    validateAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};